import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { preloadImages, loadImageAsDataUrl, getImageDimensions, groupByProductId } from "./exportImageUtils";
import { getCurrencySymbol } from "./currency";

const COMPANY = {
  name: "Better Home Fashion Inc",
  address: "280 Bowes Rd, Concord, ON L4K 1J9",
  contact: "Tel: 905-660-8741   Fax: 416-352-5796",
};

export async function exportQuoteCalculatorPdf(items, customerName, currency = "USD") {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const symbol = getCurrencySymbol(currency);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(COMPANY.name, 40, 36);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(COMPANY.address, 40, 50);
  doc.text(COMPANY.contact, 40, 62);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`Quote Calculator - Customer: ${customerName}`, 40, 82);

  const imageMap = await preloadImages(items.map((i) => i.photo_url));
  // See exportQuotationPdf.js for why: addImage stretches to the exact width/
  // height given, so a non-square photo drawn into a fixed size x size box
  // would come out visibly distorted - fit (not stretch) using its real ratio.
  const dimensionsMap = new Map(
    await Promise.all(
      [...imageMap.entries()].map(async ([url, dataUrl]) => [url, dataUrl ? await getImageDimensions(dataUrl) : null]),
    ),
  );
  const groupSize = groupByProductId(items);

  const body = items.map((i, idx) => {
    const isGroupStart = groupSize[idx] > 0;
    const variantCells = [
      { content: i.item_no || "-", styles: { fontStyle: "bold" } },
      i.size || "-",
      i.pcs_per_ctn ?? "-",
      i.ci_inventory_qty ?? "-",
      { content: i.price != null ? `${symbol}${Number(i.price).toFixed(2)}` : "-", styles: { fontStyle: "bold" } },
      i.carton_qty ?? 0,
      i.total_qty ?? 0,
      { content: `${symbol}${(i.total_amount || 0).toFixed(2)}`, styles: { fontStyle: "bold" } },
      i.cbm != null ? Number(i.cbm).toFixed(3) : "-",
    ];
    if (!isGroupStart) return variantCells;
    return [
      { content: "", rowSpan: groupSize[idx] },
      { content: i.description || "-", rowSpan: groupSize[idx] },
      ...variantCells,
    ];
  });

  const totalCbm = items.reduce((s, i) => s + (i.cbm || 0) * (i.carton_qty || 0), 0);
  const grandTotal = items.reduce((s, i) => s + (i.total_amount || 0), 0);
  body.push([
    { content: "TOTAL", colSpan: 8, styles: { fontStyle: "bold", halign: "right" } },
    { content: `${symbol}${grandTotal.toFixed(2)}`, styles: { fontStyle: "bold" } },
    { content: totalCbm.toFixed(3), styles: { fontStyle: "bold" } },
  ]);

  autoTable(doc, {
    startY: 98,
    head: [["Photo", "Description", "Item No", "Size", "Pcs/CTN", "QTY", "Price", "Carton Qty", "Total Qty", "Total Amount", "CBM"]],
    body,
    styles: { fontSize: 7.5, cellPadding: 3, lineColor: [200, 200, 200], lineWidth: 0.5, minCellHeight: 30 },
    headStyles: { fillColor: [17, 24, 39], textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 130 }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" } },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === 0 && data.row.index < items.length) {
        const url = items[data.row.index]?.photo_url;
        const dataUrl = imageMap.get(url);
        if (dataUrl) {
          try {
            const box = Math.min(data.cell.width, data.cell.height) - 6;
            const dims = dimensionsMap.get(url);
            const scale = dims ? Math.min(box / dims.width, box / dims.height) : 1;
            const width = dims ? dims.width * scale : box;
            const height = dims ? dims.height * scale : box;
            const x = data.cell.x + (data.cell.width - width) / 2;
            const y = data.cell.y + (data.cell.height - height) / 2;
            doc.addImage(dataUrl, x, y, width, height);
          } catch {
            // ignore
          }
        }
      }
    },
  });

  doc.save(`quote-calculator-${customerName}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export async function exportQuoteCalculatorExcel(items, customerName, currency = "USD") {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Quote");
  const priceNumFmt = { USD: '"$"#,##0.00', CAD: '"C$"#,##0.00', CNY: '"¥"#,##0.00' }[currency] || '"$"#,##0.00';
  sheet.columns = [
    { width: 14 }, { width: 40 }, { width: 16 }, { width: 18 },
    { width: 10 }, { width: 10 }, { width: 10 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 10 },
  ];

  [COMPANY.name, COMPANY.address, `Customer: ${customerName}`].forEach((line, idx) => {
    const rowNum = idx + 1;
    sheet.mergeCells(rowNum, 1, rowNum, 11);
    const cell = sheet.getCell(rowNum, 1);
    cell.value = line;
    cell.font = { bold: idx !== 1, size: idx === 0 ? 14 : 10 };
  });

  const headerRowNum = 5;
  const headers = ["Photo", "Description", "Item No", "Size", "Pcs/CTN", "QTY", "Price", "Carton Qty", "Total Qty", "Total Amount", "CBM"];
  headers.forEach((h, i) => {
    const cell = sheet.getRow(headerRowNum).getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF111827" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });

  const groupSize = groupByProductId(items);
  let currentRow = headerRowNum + 1;

  for (let idx = 0; idx < items.length; idx++) {
    const item = items[idx];
    const rowNum = currentRow;
    const isGroupStart = groupSize[idx] > 0;
    sheet.getRow(rowNum).height = 60;
    sheet.getCell(rowNum, 3).value = item.item_no || "-";
    sheet.getCell(rowNum, 3).font = { bold: true };
    sheet.getCell(rowNum, 4).value = item.size || "-";
    sheet.getCell(rowNum, 5).value = item.pcs_per_ctn ?? "-";
    sheet.getCell(rowNum, 6).value = item.ci_inventory_qty ?? "-";
    sheet.getCell(rowNum, 7).value = item.price ?? "-";
    sheet.getCell(rowNum, 7).font = { bold: true };
    if (item.price != null) sheet.getCell(rowNum, 7).numFmt = priceNumFmt;
    sheet.getCell(rowNum, 8).value = item.carton_qty ?? 0;
    sheet.getCell(rowNum, 9).value = item.total_qty ?? 0;
    sheet.getCell(rowNum, 10).value = item.total_amount ?? 0;
    sheet.getCell(rowNum, 10).font = { bold: true };
    sheet.getCell(rowNum, 10).numFmt = priceNumFmt;
    sheet.getCell(rowNum, 11).value = item.cbm ?? "-";

    if (isGroupStart) {
      const span = groupSize[idx];
      if (span > 1) {
        sheet.mergeCells(rowNum, 1, rowNum + span - 1, 1);
        sheet.mergeCells(rowNum, 2, rowNum + span - 1, 2);
      }
      sheet.getCell(rowNum, 2).value = item.description || "-";
      sheet.getCell(rowNum, 2).alignment = { wrapText: true, vertical: "middle" };
      if (item.photo_url) {
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await loadImageAsDataUrl(item.photo_url);
        if (dataUrl) {
          const match = dataUrl.match(/^data:image\/(\w+);base64,(.*)$/);
          if (match) {
            const ext = match[1] === "png" ? "png" : "jpeg";
            // eslint-disable-next-line no-await-in-loop
            const dims = await getImageDimensions(dataUrl);
            const box = 70;
            const scale = dims ? Math.min(box / dims.width, box / dims.height) : 1;
            const width = dims ? dims.width * scale : box;
            const height = dims ? dims.height * scale : box;
            const imageId = workbook.addImage({ base64: dataUrl, extension: ext });
            sheet.addImage(imageId, { tl: { col: 0.1, row: rowNum - 1 + 0.1 }, ext: { width, height } });
          }
        }
      }
    }

    for (let col = 1; col <= 11; col++) {
      sheet.getCell(rowNum, col).border = {
        top: { style: "thin", color: { argb: "FFD1D5DB" } },
        bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
        left: { style: "thin", color: { argb: "FFD1D5DB" } },
        right: { style: "thin", color: { argb: "FFD1D5DB" } },
      };
    }
    currentRow += 1;
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `quote-calculator-${customerName}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
